import { useCallback, useEffect, useState } from "react";

import { useDeployment, useWallet } from "../../context";
import {
  V3_FACTORY_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
  V3_POOL_ABI,
} from "../../lib/abis";
import { ZERO_ADDRESS } from "../../lib/constants";

import type { V3Position } from "./types";
import type { Address } from "viem";

export function useV3Positions() {
  const { deployment } = useDeployment();
  const { address, publicClient } = useWallet();

  const [positions, setPositions] = useState<V3Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [poolTicks, setPoolTicks] = useState<Record<string, number>>({});

  const fetchPositions = useCallback(async () => {
    if (!address || !deployment?.v3) return;
    setLoading(true);
    try {
      const npm = deployment.v3.nonfungiblePositionManager;

      // Phase 1: Get position count
      const count = await publicClient.readContract({
        address: npm,
        abi: V3_NFT_POSITION_MANAGER_ABI,
        functionName: "balanceOf",
        args: [address],
      });

      const numPositions = Number(count);
      if (numPositions === 0) {
        setPositions([]);
        setLoading(false);
        return;
      }

      // Phase 2: Fetch all tokenIds in parallel
      const tokenIds = await Promise.all(
        Array.from({ length: numPositions }, (_, i) =>
          publicClient.readContract({
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "tokenOfOwnerByIndex",
            args: [address, BigInt(i)],
          }),
        ),
      );

      // Phase 3: Fetch all position data in parallel
      const posDataResults = await Promise.all(
        tokenIds.map((tokenId) =>
          publicClient.readContract({
            address: npm,
            abi: V3_NFT_POSITION_MANAGER_ABI,
            functionName: "positions",
            args: [tokenId],
          }),
        ),
      );

      const result: V3Position[] = tokenIds.map((tokenId, i) => {
        const pos = posDataResults[i] as readonly [
          bigint,
          Address,
          Address,
          Address,
          number,
          number,
          number,
          bigint,
          bigint,
          bigint,
          bigint,
          bigint,
        ];
        return {
          tokenId,
          token0: pos[2],
          token1: pos[3],
          fee: pos[4],
          tickLower: pos[5],
          tickUpper: pos[6],
          liquidity: pos[7],
          feeGrowthInside0LastX128: pos[8],
          feeGrowthInside1LastX128: pos[9],
          tokensOwed0: pos[10],
          tokensOwed1: pos[11],
        };
      });

      setPositions(result);

      // Phase 4: Fetch unique pool ticks in parallel
      if (result.length > 0) {
        const uniquePools = new Map<
          string,
          { token0: Address; token1: Address; fee: number }
        >();
        for (const p of result) {
          const key = `${p.token0}:${p.token1}:${String(p.fee)}`;
          if (!uniquePools.has(key)) {
            uniquePools.set(key, {
              token0: p.token0,
              token1: p.token1,
              fee: p.fee,
            });
          }
        }

        const tickResults = await Promise.allSettled(
          [...uniquePools.entries()].map(async ([key, pool]) => {
            const poolAddr = await publicClient.readContract({
              address: deployment.v3.factory,
              abi: V3_FACTORY_ABI,
              functionName: "getPool",
              args: [pool.token0, pool.token1, pool.fee],
            });
            if (poolAddr === ZERO_ADDRESS) return null;
            const slot0 = (await publicClient.readContract({
              address: poolAddr,
              abi: V3_POOL_ABI,
              functionName: "slot0",
            })) as readonly [
              bigint,
              number,
              number,
              number,
              number,
              number,
              boolean,
            ];
            return { key, tick: slot0[1] };
          }),
        );

        const ticks: Record<string, number> = {};
        for (const r of tickResults) {
          if (r.status === "fulfilled" && r.value) {
            ticks[r.value.key] = r.value.tick;
          }
        }

        setPoolTicks((prev) => {
          const keys = Object.keys(ticks);
          if (
            keys.length === Object.keys(prev).length &&
            keys.every((k) => prev[k] === ticks[k])
          ) {
            return prev;
          }
          return ticks;
        });
      }
    } catch {
      // Position reading may fail if no positions
    } finally {
      setLoading(false);
    }
  }, [address, deployment?.v3, publicClient]);

  useEffect(() => {
    void fetchPositions();
  }, [fetchPositions]);

  return { positions, loading, poolTicks, refetch: fetchPositions };
}
